import { html, SP } from '../../../html.js';
import type { VNode } from '../../../html.js';
import { CopyButton } from '../../../ui/copy.js';
import type { TenantDetail } from '../../api.js';
import type { DashboardActions, DashboardState } from '../../store.js';
import { ConnectCard, Platform, SUPPORTED_PROTOCOL_VERSIONS, Snippet, WhyKeyless } from '../connect.js';
import {
  curlInitialize,
  integrationGuide,
  proxyCaddy,
  proxyCloudflareWorker,
  proxyNginx,
  proxyPaths,
  proxyVercel,
} from '../../../ui/endpoints.js';
import type { ProxyTarget } from '../../../ui/endpoints.js';
import { ErrorNote } from '../../ui/bits.js';

/**
 * Tab four: everything about reaching this site from outside ClawHarbor.
 *
 * Three ways in, in the order people ask for them. An AI client adds the hosted
 * endpoint (the connector card); the site's own pages register the same tools
 * for browser agents (the embed tag); and the endpoint moves under the
 * business's own domain, `https://<their host>/mcp`, by proxying three paths
 * here (the proxy section). The third used to be one sentence pointing at a
 * markdown file the customer could not open.
 *
 * The whole tab is also one document: "Copy the integration guide" hands over
 * the same snippets as Markdown for whoever runs the website, built by the same
 * functions that draw this screen so the two cannot drift.
 */

/**
 * The tag, and the honest paragraph about who will see the tools it registers.
 *
 * WebMCP is a browser API. An agent that does not execute page scripts cannot
 * see anything the tag registers, and there is no standard yet by which a
 * website points such an agent at a hosted endpoint — so the hosted MCP URL is
 * set beside the tag as the other half of the answer, and neither is sold as
 * the whole of it.
 *
 * Built from `urls.embedJs` and `urls.embed` as the SERVER sent them, never
 * from a host name in this bundle: a dashboard served behind a proxy with a
 * different public name would otherwise hand every tenant a tag pointing at the
 * wrong origin, and the tag is the one thing here a person pastes into somebody
 * else's page and walks away from.
 */
function EmbedSection(props: {
  t: TenantDetail;
  state: DashboardState;
  actions: DashboardActions;
}): VNode {
  const { t, state, actions } = props;
  const tag = `<script src="${t.urls.embedJs}" async></script>`;
  return html`
    <section class="embed" aria-labelledby="embed-h">
      <h2 class="sec__h" id="embed-h">2. On ${t.source.canonicalOrigin}, through WebMCP</h2>
      <p class="sec__p">
        One tag registers this site's tools on any page of the site itself, for browser agents that
        expose WebMCP. The page stays the interface; the tools are the whole contribution. The
        script never writes to the page, and on a browser without WebMCP it does nothing at all.
      </p>
      <div class="snippet">
        <div class="snippet__bar">
          <span class="snippet__name">Add to any page on ${t.source.canonicalOrigin}</span>
          ${CopyButton({
            id: `embed-${t.tenantId}`,
            text: tag,
            copied: state.copied,
            onCopy: actions.copy,
            label: 'Copy tag',
            describe: `the embed tag for ${t.tenantId}`,
          })}
        </div>
        <pre class="snippet__code"><code>${tag}</code></pre>
      </div>
      <p class="embed__link">
        <a href=${t.urls.embed} target="_blank" rel="noopener noreferrer">${t.urls.embed}</a>${SP}
        — what the tag does, in full, as a page to hand to whoever maintains the site.
      </p>

      <div class="embed__paths">
        <div class="embed__path">
          <p class="cardlabel">Browser agents</p>
          <p class="embed__p">
            Agents running inside a browser that exposes WebMCP — Chrome behind the origin trial or
            the <code>enable-webmcp-testing</code> flag, on an https page — see the tools this tag
            registers, on the site's own domain. That is the whole audience of the tag today.
          </p>
        </div>
        <div class="embed__path">
          <p class="cardlabel">Everything else</p>
          <p class="embed__p">
            An agent that does not execute page scripts — claude.ai, an API client, a crawler —
            will not see them. For those the path is the hosted MCP endpoint, given to the agent
            directly, because there is no standard yet by which a website advertises one:
          </p>
          <div class="url">
            <span class="url__tag">MCP</span>
            <a class="url__value" href=${t.urls.mcp} target="_blank" rel="noopener noreferrer">
              ${t.urls.mcp}
            </a>
            ${CopyButton({
              id: `embed-mcp-${t.tenantId}`,
              text: t.urls.mcp,
              copied: state.copied,
              onCopy: actions.copy,
              describe: `the MCP endpoint for ${t.tenantId}`,
            })}
          </div>
        </div>
      </div>
      <p class="embed__foot">
        Both call the same data plane and answer with the same envelope. Neither can write, book,
        pay or authenticate.
      </p>
    </section>
  `;
}

/** `hep.hr` is covered by a claim on `hep.hr`, and so is `www.hep.hr`. */
function coveredBy(host: string, domain: string | null): boolean {
  if (!domain) return false;
  const d = domain.toLowerCase().replace(/^www\./, '');
  return host === d || host.endsWith(`.${d}`);
}

function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

/**
 * `/mcp` on the business's own domain.
 *
 * TWO KEYS, AND THE PANEL SAYS WHICH ONE IS MISSING. routes/mcp.ts answers a
 * browser on a proxy origin only when the host is declared here AND a verified,
 * unexpired ownership claim covers its domain. Handing out proxy configs
 * without saying so would teach a third-party tenant to build a proxy that
 * refuses every browser request with a bare 403, so the gate comes first and
 * the snippets second. Non-browser clients work through the proxy either way,
 * and the panel says that too, because it is the reason a curl check passes
 * while a browser fails.
 *
 * The snippets are written for the first declared host, or for the business's
 * own host while none is declared: a finished config for the real name is the
 * fastest way to explain what declaring one does. The claim is proved on the
 * Overview tab, where the challenge UI already lives; this panel links there
 * rather than keeping a second copy of a stateful flow.
 */
function ProxySection(props: { t: TenantDetail; state: DashboardState; actions: DashboardActions }): VNode {
  const { t, state, actions } = props;
  const declared = t.proxyHosts;
  const own = hostOf(t.source.canonicalOrigin);
  const host = declared[0] ?? own;
  const claim = t.ownership;
  const claimed = !!claim?.current && coveredBy(host, claim.domain);
  const target: ProxyTarget = {
    host,
    mcp: t.urls.mcp,
    protectedResourceMetadata: t.urls.protectedResourceMetadata,
    icon: t.urls.icon,
  };
  const ctx = { copied: state.copied, onCopy: actions.copy };
  const draft = state.proxyDraft ?? declared.join(', ');
  const dirty = state.proxyDraft !== null && state.proxyDraft.trim() !== declared.join(', ');
  const overview = `#/sites/${encodeURIComponent(t.tenantId)}`;
  const bare = own.replace(/^www\./, '');

  return html`
    <section class="proxy" aria-labelledby="proxy-h">
      <h2 class="sec__h" id="proxy-h">3. As https://${host}/mcp</h2>
      <p class="sec__p">
        The business's own address says something a ClawHarbor URL does not. Their web server
        forwards three paths here and ClawHarbor does the rest: tool calls, widgets, freshness, the
        data plane. Widgets keep loading from ClawHarbor on purpose, since their security policy
        names that origin.
      </p>

      <ol class="keys">
        <li class=${declared.length ? 'key key--ok' : 'key'}>
          <b class="key__h">Declare the hostname.</b>${SP}
          ${declared.length
            ? html`Declared: ${declared.map((h, i) => html`${i ? ', ' : ''}<code>${h}</code>`)}.`
            : bare.split('.').length === 2
              ? html`None yet. Up to five bare hostnames, such as <code>${bare}</code> and <code>www.${bare}</code>.`
              : html`None yet. Up to five bare hostnames, such as <code>${bare}</code>.`}
          <form
            class="codeform"
            onSubmit=${(e: Event) => {
              e.preventDefault();
              actions.saveProxyHosts();
            }}
          >
            <label class="cardlabel" for="proxy-hosts">Hostnames that forward /mcp here</label>
            <div class="codeform__row">
              <input
                id="proxy-hosts"
                class="codeform__input codeform__input--wide"
                type="text"
                spellcheck="false"
                autocomplete="off"
                placeholder=${bare}
                value=${draft}
                onInput=${(e: Event) => actions.editProxyHosts((e.target as HTMLInputElement).value)}
              />
              <button type="submit" class="btn" disabled=${!dirty || state.savingProxy}>
                ${state.savingProxy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
          ${ErrorNote({ message: state.proxyError })}
        </li>
        <li class=${claimed ? 'key key--ok' : 'key'}>
          <b class="key__h">Prove the domain.</b>${SP}
          ${claimed
            ? html`Verified for <code>${claim?.domain}</code>${claim?.daysUntilExpiry != null ? `, ${claim.daysUntilExpiry} days left` : ''}.`
            : claim?.domain && claim.current
              ? html`The verified claim is for <code>${claim.domain}</code>, which does not cover <code>${host}</code>.`
              : html`No current claim. One DNS record, a file or a meta tag does it: <a href=${overview}>prove it on Overview</a>.`}
        </li>
      </ol>
      <p class="sec__p muted">
        ${declared.length && claimed
          ? 'Both keys are in place: browsers on this domain are answered.'
          : 'Until both hold, the proxy works for AI clients, servers and curl, and a browser on the domain gets "403 origin not allowed". That is deliberate: nobody opens an origin on a name they have not proved.'}
      </p>

      <table class="paths">
        <thead><tr><th scope="col">Your path</th><th scope="col">Forwards to</th></tr></thead>
        <tbody>
          ${proxyPaths(target).map(
            (p) => html`<tr>
              <th scope="row"><code>${p.from}</code></th>
              <td><code class="paths__to">${p.to}</code><span class="paths__note">${p.note}</span></td>
            </tr>`,
          )}
        </tbody>
      </table>

      <p class="sec__p">
        Forward <code>Content-Type</code>, <code>Accept</code>, <code>Mcp-Session-Id</code>,${SP}
        <code>Mcp-Protocol-Version</code>, <code>Mcp-Method</code>, <code>Mcp-Name</code> and${SP}
        <code>Origin</code> unchanged, both ways. Do not answer <code>OPTIONS</code> yourself, do not
        rewrite the body, do not buffer or cache. Each config below already does that.
      </p>

      ${Platform({
        title: 'nginx',
        open: true,
        children: Snippet({ id: `nginx-${t.tenantId}`, name: `inside the server block for ${host}`, text: proxyNginx(target), describe: 'the nginx config', ctx }),
      })}
      ${Platform({
        title: 'Caddy',
        children: Snippet({ id: `caddy-${t.tenantId}`, name: 'Caddyfile', text: proxyCaddy(target), describe: 'the Caddy config', ctx }),
      })}
      ${Platform({
        title: 'Cloudflare Worker',
        children: Snippet({ id: `worker-${t.tenantId}`, name: 'worker.js', text: proxyCloudflareWorker(target), describe: 'the Cloudflare Worker', ctx }),
      })}
      ${Platform({
        title: 'Vercel',
        children: html`
          ${Snippet({ id: `vercel-${t.tenantId}`, name: 'vercel.json', text: proxyVercel(target), describe: 'the Vercel rewrites', ctx })}
          <p class="embed__foot">
            Whether an external rewrite streams <code>text/event-stream</code> unbuffered is
            unverified. The current protocol revision is plain POST, so only legacy clients use that
            stream.
          </p>
        `,
      })}
      ${Platform({
        title: 'Check it',
        children: html`
          <p class="embed__p">The handshake an AI client makes first, against the business's own address:</p>
          ${Snippet({
            id: `proxy-curl-${t.tenantId}`,
            name: 'initialize',
            text: curlInitialize(`https://${host}/mcp`, SUPPORTED_PROTOCOL_VERSIONS[1]),
            label: 'Copy curl',
            describe: 'the curl check against your own domain',
            ctx,
          })}
        `,
      })}
    </section>
  `;
}

export function ConnectTab(props: {
  t: TenantDetail;
  state: DashboardState;
  actions: DashboardActions;
  /** The origin this page was served from; the connector URLs are built on it. */
  origin: string;
}): VNode {
  const { t, state, actions, origin } = props;
  const host = t.proxyHosts[0] ?? hostOf(t.source.canonicalOrigin);
  const guide = integrationGuide({
    displayName: t.identity.displayName,
    tenantId: t.tenantId,
    canonicalOrigin: t.source.canonicalOrigin,
    mcp: t.urls.mcp,
    embedJs: t.urls.embedJs,
    proxy: { host, mcp: t.urls.mcp, protectedResourceMetadata: t.urls.protectedResourceMetadata, icon: t.urls.icon },
    protocolVersion: SUPPORTED_PROTOCOL_VERSIONS[1],
  });
  return html`
    <div class="tabpanel">
      <div class="guidebar">
        <p class="guidebar__p">
          Three ways in. Handing this to whoever runs the website? Copy all of it as one document.
        </p>
        ${CopyButton({
          id: `guide-${t.tenantId}`,
          text: guide,
          copied: state.copied,
          onCopy: actions.copy,
          label: 'Copy the integration guide',
          describe: `the integration guide for ${t.tenantId}, as Markdown`,
        })}
      </div>

      <h2 class="sec__h">1. In an AI client</h2>
      <ul class="conlist">
        ${ConnectCard({
          tenantId: t.tenantId,
          displayName: t.identity.displayName,
          tools: t.capabilities.length,
          origin,
          ctx: { copied: state.copied, onCopy: actions.copy },
        })}
      </ul>
      ${WhyKeyless()}
      ${EmbedSection({ t, state, actions })}
      ${ProxySection({ t, state, actions })}
    </div>
  `;
}
